/**
 * OAuthCallback — handles redirect from GitHub / LinkedIn
 *
 * URL: /auth/callback?code=xxx&state=yyy&provider=github
 *
 * Flow:
 *   1. Provider redirects here with ?code=xxx
 *   2. Read `provider` from state (stored in sessionStorage before redirect)
 *   3. POST code to backend → backend exchanges for token → returns user
 *   4. Redirect to appropriate dashboard
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSocialAuth } from '../hooks/useSocialAuth';
import { DASHBOARD_ROUTES } from '../context/AuthContext';

export default function OAuthCallback() {
  const [searchParams]    = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing | error
  const [message, setMessage] = useState('');
  const navigate            = useNavigate();
  const { handleCallback }  = useSocialAuth();

  useEffect(() => {
    const code     = searchParams.get('code');
    const state    = searchParams.get('state');
    const error    = searchParams.get('error');

    // OAuth error (user denied permission)
    if (error) {
      setStatus('error');
      setMessage(error === 'access_denied'
        ? 'You cancelled the sign-in. Please try again.'
        : `Authentication failed: ${error}`
      );
      setTimeout(() => navigate('/'), 3000);
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('No authorisation code received. Please try again.');
      setTimeout(() => navigate('/'), 3000);
      return;
    }

    // Parse provider from state JWT
    let provider = 'github';
    try {
      const decoded = JSON.parse(atob(state));
      provider = decoded.provider || 'github';
    } catch (_) {}

    // Exchange code for user
    handleCallback(code, provider).then((u) => {
      if (u) {
        const route = DASHBOARD_ROUTES[u.role] || '/';
        navigate(route, { replace: true });
      } else {
        setStatus('error');
        setMessage('Sign-in failed. Please try again or use email.');
        setTimeout(() => navigate('/'), 3000);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#f8fafc',
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: '48px 40px',
        boxShadow: '0 8px 40px rgba(0,0,0,.1)', textAlign: 'center',
        maxWidth: 420, width: '90%',
      }}>
        {status === 'processing' ? (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>
              <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⚙️</span>
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.2rem', color: '#1a1a2e' }}>
              Signing you in…
            </h2>
            <p style={{ color: '#6b7280', fontSize: '.88rem', margin: 0 }}>
              Verifying your account. Please wait.
            </p>
          </>
        ) : (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>❌</div>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.2rem', color: '#dc2626' }}>
              Sign-in Failed
            </h2>
            <p style={{ color: '#6b7280', fontSize: '.88rem', margin: '0 0 16px' }}>
              {message}
            </p>
            <p style={{ color: '#9ca3af', fontSize: '.78rem', margin: 0 }}>
              Redirecting you back…
            </p>
          </>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
